import { Badge, Dropdown, Menu, message, type MenuProps } from "antd";
import type { Customer } from "@zhihaoscm/entity";
import { MEMBER_LEVEL, MEMBER_STATE } from "@zhihaoscm/meta";
import { useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { loginService } from "@/api/loginService";
import { isChainCloudTenant } from "@/api/legacy";
import { ChatAI } from "@/components/chatAI/ChatAI";
import { defaultTheme, runtimePublicConfig } from "@/runtime/config";
import { AccountType, useAccountSession, useUserSession } from "@/stores/session";
import "@/layouts/console-layout.css";

const CDN_URL = "https://static.zhihaoscm.cn/";
const consoleLogo = `${CDN_URL}logo/shaheshang-logo.png`;
const defaultAvatar = `${CDN_URL}customer-admin/avatar.svg`;

/** 与旧 Vue Header 的 getMemberImg 保持一致：过期及非会员不展示等级勋章。 */
export function getMemberMedalUrl(customer?: Customer) {
    if (!customer || customer.memberState === MEMBER_STATE.EXPIRY.code) return "";
    if (customer.memberLevel === MEMBER_LEVEL.BASE.code) return `${CDN_URL}customer-admin/base-level-medal.svg`;
    if (customer.memberLevel === MEMBER_LEVEL.SENIOR.code) return `${CDN_URL}customer-admin/senior-level-medal.svg`;
    return "";
}

interface NavigationGroup {
    icon: string;
    key: string;
    label: string;
    routes: Array<{ label: string; path: string }>;
}

interface NavigationRoute {
    label: string;
    path: string;
}

const navigationGroups: NavigationGroup[] = [
    {
        icon: "icon-files",
        key: "me",
        label: "账号管理",
        routes: [
            { label: "个人信息", path: "/me/info" },
            { label: "安全设置", path: "/me/safeSetting" },
            { label: "认证", path: "/me/authentication" },
            { label: "子账号管理", path: "/me/subAccountManagement" },
            { label: "受邀记录", path: "/me/invitationRecord" },
            { label: "短信管理", path: "/me/SMSManagement" },
            { label: "发票抬头", path: "/me/invoice" },
            { label: "我的地址", path: "/me/address" },
            { label: "银行账户", path: "/me/bankAccount" }
        ]
    },
    {
        icon: "icon-files",
        key: "orderCenter",
        label: "交易中心",
        routes: [
            { label: "我的订单", path: "/orderCenter/myOrder" },
            { label: "我的发票", path: "/orderCenter/myInvoice" }
        ]
    },
    {
        icon: "icon-logistics",
        key: "logistics",
        label: "物流中心",
        routes: [
            { label: "查船", path: "/logistics/findLocation" },
            { label: "查车", path: "/logistics/truckLocation" },
            { label: "运单", path: "/logistics/shipOrder" },
            { label: "船务信息服务费", path: "/logistics/shipInfoServiceFee" }
        ]
    },
    {
        icon: "icon-aixin-1",
        key: "followCenter",
        label: "关注中心",
        routes: [
            { label: "我的足迹", path: "/followCenter/footprints" },
            { label: "我的收藏", path: "/followCenter/favorite" },
            { label: "我的关注", path: "/followCenter/interest" },
            { label: "我的预警", path: "/followCenter/priceWarning" }
        ]
    }
];

const rootAccountOnlyPaths = new Set(["/me/subAccountManagement", "/me/SMSManagement"]);
const nonSubAccountPaths = new Set(["/me/invitationRecord", "/me/invoice", "/me/address"]);
const chatAIHiddenPaths = new Set(["/logistics/findLocation", "/logistics/truckLocation"]);

/** 旧 Vue 的两个地图页会临时隐藏链小云浮窗，离开页面后恢复显示。 */
export function shouldShowChatAI(pathname: string) {
    return isChainCloudTenant() && !chatAIHiddenPaths.has(pathname);
}

/**
 * 账号菜单严格复刻旧 Vue 的账号类型矩阵：主账号看到管理入口，子账号只看到自己的账号信息。
 * 普通账号及账号类型尚未初始化时不能提前暴露主账号入口。
 */
export function getAccountNavigationRoutes(accountType?: number): NavigationRoute[] {
    const isRootAccount = accountType === AccountType.ROOT_ACCOUNT;
    const isSubAccount = accountType === AccountType.SUB_ACCOUNT;
    const routes = navigationGroups[0].routes.filter(route => {
        if (rootAccountOnlyPaths.has(route.path)) return isRootAccount;
        if (nonSubAccountPaths.has(route.path)) return !isSubAccount;
        return true;
    });

    if (isSubAccount) {
        routes.splice(3, 0, { label: "子账号信息", path: "/me/subAccountManagement/detail" });
    }
    return routes;
}

/** 旧菜单仅在账号域展示账号项，控制台业务域展示交易、物流、关注和报名。 */
function buildMenuItems(pathname: string, accountType?: number): MenuProps["items"] {
    if (pathname.startsWith("/me/")) {
        const accountGroup = navigationGroups[0];
        const routes = getAccountNavigationRoutes(accountType);
        const items: NonNullable<MenuProps["items"]> = [
            {
                key: accountGroup.key,
                icon: <i className={`iconfont ${accountGroup.icon}`} />,
                label: accountGroup.label,
                children: routes.map(route => ({ key: route.path, label: <Link to={route.path}>{route.label}</Link> }))
            }
        ];
        // 旧 Vue 的“我的会员”只在链云砂石租户展示。
        if (isChainCloudTenant()) {
            items.push({
                key: "/me/member",
                icon: <i className="iconfont icon-a-member1" />,
                label: <Link to="/me/member">我的会员</Link>
            });
        }
        return items;
    }
    return [
        ...navigationGroups
            .slice(1)
            .map(group => ({
                key: group.key,
                icon: <i className={`iconfont ${group.icon}`} />,
                label: group.label,
                children: group.routes.map(route => ({
                    key: route.path,
                    label: <Link to={route.path}>{route.label}</Link>
                }))
            })),
        {
            key: "/registration/myRegistration",
            icon: <i className="iconfont icon-registration" />,
            label: <Link to="/registration/myRegistration">我的报名</Link>
        }
    ];
}

export function ConsoleLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const [collapsed, setCollapsed] = useState(false);
    const user = useUserSession(state => state.userInfo.customer);
    const unread = useUserSession(state => state.allUnreadMsgNum);
    const clearUser = useUserSession(state => state.clear);
    const accountType = useAccountSession(state => state.accountType);
    const proxyActualAccount = useAccountSession(state => state.proxyActualAccount);
    const menuItems = useMemo(() => buildMenuItems(location.pathname, accountType), [accountType, location.pathname]);
    const activePath = `/${location.pathname.split("/").filter(Boolean).slice(0, 2).join("/")}`;
    const openKey = location.pathname.startsWith("/me/") ? "me" : location.pathname.split("/")[1];
    const showConsoleButton = location.pathname.startsWith("/me/") || location.pathname.startsWith("/userMessage");
    const showBackHome = /^\/(orderCenter|logistics|followCenter)/.test(location.pathname);
    const userName = user?.institutionName || user?.realName || user?.mobile || "未登录";
    const memberMedalUrl = isChainCloudTenant() ? getMemberMedalUrl(proxyActualAccount) : "";

    /** 退出后清空与 Vue 相同的用户上下文，再返回统一主站。 */
    const logout = async () => {
        await loginService.logout();
        clearUser();
        void message.success("已退出登录");
        window.location.href = runtimePublicConfig.originUrl || "/";
    };
    const userItems: MenuProps["items"] = [
        {
            key: "profile",
            icon: <i className="iconfont icon-_tuceng_11" />,
            label: "账号管理",
            onClick: () => navigate("/me/info")
        },
        {
            key: "member",
            icon: <i className="iconfont icon-wodehuiyuan" />,
            label: "我的会员",
            onClick: () => navigate("/me/member")
        },
        { type: "divider" },
        { key: "logout", icon: <i className="iconfont icon-tuichu" />, label: "退出登录", onClick: () => void logout() }
    ];

    return (
        <div className="console-shell">
            <header className="console-header">
                <div className="console-header-left">
                    <button
                        className="console-brand"
                        onClick={() => {
                            window.location.href = runtimePublicConfig.originUrl || "/";
                        }}
                        type="button"
                    >
                        <img alt="" className="console-logo" src={consoleLogo} />
                        <span>{defaultTheme.systemName}</span>
                    </button>
                    {showConsoleButton ? (
                        <button
                            className="console-header-link console-header-link--console"
                            onClick={() => navigate("/logistics/findLocation")}
                            type="button"
                        >
                            控制台
                        </button>
                    ) : null}
                    {showBackHome ? (
                        <button
                            className="console-header-link"
                            onClick={() => {
                                window.location.href = runtimePublicConfig.originUrl || "/";
                            }}
                            type="button"
                        >
                            返回首页
                        </button>
                    ) : null}
                </div>
                <div className="console-header-actions">
                    <Badge count={unread} size="small">
                        <button
                            aria-label="消息提醒"
                            className="console-message"
                            onClick={() => navigate("/userMessage/")}
                            type="button"
                        >
                            <svg aria-hidden="true" className="console-message-icon" viewBox="0 0 1024 1024">
                                <path d="M702.0032 102.4H322.048C172.8 102.4 51.2 223.5392 51.2 372.736v285.4912c0 149.1456 121.6 270.2848 270.7968 270.2848H972.8V372.6848C972.8 223.5392 851.2 102.4 702.0032 102.4zm204.288 760.064H321.9968a204.4928 204.4928 0 01-204.288-203.776V372.6848c0-112.5888 91.648-203.776 204.288-203.776h380.0064c112.64 0 204.288 91.648 204.288 203.776v489.7792z" />
                                <path d="M702.0032 468.1728h-95.0272V563.2h95.0272V468.1728zM416.9728 468.1728H322.048V563.2h94.976V468.1728z" />
                            </svg>
                        </button>
                    </Badge>
                    <Dropdown
                        menu={{ items: userItems }}
                        placement="bottomRight"
                        dropdownRender={() => (
                            <div className="console-user-popover">
                                <div className="console-user-popover-head">
                                    <img alt="用户头像" className="console-user-popover-avatar" src={defaultAvatar} />
                                    <div className="console-user-popover-info">
                                        <div className="console-user-popover-name">
                                            <strong>{userName}</strong>
                                            {user?.institutionName || user?.realName ? <i className="iconfont icon-yirenzheng" /> : null}
                                            {memberMedalUrl ? <img alt="会员标识" className="console-user-popover-medal" src={memberMedalUrl} /> : null}
                                        </div>
                                        <span>{user?.mobile || useUserSession.getState().mobile}</span>
                                    </div>
                                </div>
                                <div className="console-user-popover-line" />
                                <button type="button" onClick={() => navigate("/me/info")}><i className="iconfont icon-_tuceng_11" />账号管理</button>
                                <button type="button" onClick={() => navigate("/me/member")}><i className="iconfont icon-wodehuiyuan" />我的会员</button>
                                <button type="button"><i className="iconfont icon-qiehuanqiye" />切换企业</button>
                                <div className="console-user-popover-line" />
                                <button type="button" onClick={() => void logout()}><i className="iconfont icon-tuichu" />退出登录</button>
                            </div>
                        )}
                    >
                        <button className="console-user" type="button">
                            <span>{userName}</span>
                            {memberMedalUrl ? <img alt="会员标识" className="console-member-level" src={memberMedalUrl} /> : null}
                            <img alt="用户头像" className="console-avatar" src={defaultAvatar} />
                        </button>
                    </Dropdown>
                </div>
            </header>
            <main className="console-body">
                {!collapsed ? (
                    <aside className="console-menu-wrap">
                        {shouldShowChatAI(location.pathname) ? <ChatAI /> : null}
                        <Menu
                            className="console-menu"
                            defaultOpenKeys={[openKey]}
                            items={menuItems}
                            mode="inline"
                            selectedKeys={[activePath]}
                        />
                        <button className="console-collapse" onClick={() => setCollapsed(true)} type="button">
                            <i className="iconfont icon-a-putaway" />
                            <span>收起导航</span>
                        </button>
                    </aside>
                ) : (
                    <button
                        aria-label="展开导航"
                        className="console-expand"
                        onClick={() => setCollapsed(false)}
                        type="button"
                    >
                        <i className="iconfont icon-a-putaway1" />
                    </button>
                )}
                <section className="console-content">
                    <Outlet />
                </section>
            </main>
        </div>
    );
}
